import { useState } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const quickLinks = categories.slice(0, 6);

  return (
    <footer className="relative bg-accent-900 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none select-none flex items-center justify-center">
        <p className="font-heading text-accent-800/20 text-6xl md:text-9xl font-black tracking-tighter whitespace-nowrap">
          ACBUYSPREADSHEET
        </p>
      </div>

      <div className="relative z-10 px-4 md:px-6 py-16 md:py-20 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
          <div className="lg:col-span-1">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-6">
              ACBuySpreadsheet Hub
            </h3>
            <p className="text-sm text-background-50/60 leading-relaxed">
              The most trusted knowledge base and guide platform for
              acbuyspreadsheet systems, tools, and usage strategies.
            </p>
          </div>

          <div>
            <h4 className="font-label text-sm font-semibold text-background-50 mb-5 uppercase tracking-wider">
              Quick Links
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-sm text-background-50/60 hover:text-background-50 transition-colors"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/what-is-acbuyspreadsheet"
                  className="text-sm text-background-50/60 hover:text-background-50 transition-colors"
                >
                  Guides
                </Link>
              </li>
              <li>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-background-50/60 hover:text-background-50 transition-colors"
                >
                  Main Site
                </a>
              </li>
              {quickLinks.map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-background-50/60 hover:text-background-50 transition-colors"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-label text-sm font-semibold text-background-50 mb-5 uppercase tracking-wider">
              Social
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="#"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 text-sm text-background-50/60 hover:text-background-50 transition-colors"
                >
                  <i className="ri-instagram-line text-base w-5 h-5 flex items-center justify-center"></i>
                  Instagram
                </a>
              </li>
              <li>
                <a
                  href="#"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 text-sm text-background-50/60 hover:text-background-50 transition-colors"
                >
                  <i className="ri-youtube-line text-base w-5 h-5 flex items-center justify-center"></i>
                  YouTube
                </a>
              </li>
              <li>
                <a
                  href="#"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 text-sm text-background-50/60 hover:text-background-50 transition-colors"
                >
                  <i className="ri-facebook-line text-base w-5 h-5 flex items-center justify-center"></i>
                  Facebook
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-label text-sm font-semibold text-background-50 mb-5 uppercase tracking-wider">
              Stay Updated
            </h4>
            <p className="text-sm text-background-50/60 mb-4 leading-relaxed">
              Get the latest ACBuySpreadsheet guides and tips delivered to your
              inbox.
            </p>
            <form
              data-readdy-form
              action="https://readdy.ai/api/form/d8tpd6ob9jno5e72bo40"
              method="POST"
              encType="application/x-www-form-urlencoded"
              className="flex"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                const honeypotVal = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
                if (honeypotVal) {
                  setSubmitStatus("success");
                  setEmail("");
                  return;
                }
                try {
                  const params = new URLSearchParams();
                  formData.forEach((val, key) => {
                    if (key !== "website_alt") {
                      params.append(key, val as string);
                    }
                  });
                  await fetch(form.action, { method: "POST", body: params });
                  setSubmitStatus("success");
                  setEmail("");
                } catch {
                  setSubmitStatus("error");
                }
              }}
            >
              <input
                type="email"
                name="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 px-4 py-2.5 rounded-l-full bg-background-50/10 border border-background-50/20 text-background-50 text-sm placeholder:text-background-50/40 outline-none focus:border-background-50/50 transition-colors"
              />
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hp-field"
              />
              <button
                type="submit"
                className="px-4 py-2.5 rounded-r-full bg-background-50 text-accent-900 cursor-pointer hover:bg-background-100 transition-colors flex items-center justify-center"
              >
                <i className="ri-arrow-right-line text-base w-5 h-5 flex items-center justify-center"></i>
              </button>
              {submitStatus === "success" && (
                <p className="absolute -bottom-6 left-0 text-xs text-green-300">Subscribed!</p>
              )}
              {submitStatus === "error" && (
                <p className="absolute -bottom-6 left-0 text-xs text-red-300">Try again later.</p>
              )}
            </form>
          </div>
        </div>

        <div className="mt-12 md:mt-16 pt-8 border-t border-background-50/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-background-50/40">
            &copy; {new Date().getFullYear()} ACBuySpreadsheet Hub. All rights
            reserved.
          </p>
          <p className="text-xs text-background-50/40">
            We provide educational content about acbuyspreadsheet systems. We do
            not claim ownership of external tools or data sources.
          </p>
        </div>
      </div>
    </footer>
  );
}